import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { VariablePalette } from '@/components/admin/protocoles/step/variable-palette';
import { PaintedTextField } from '@/components/admin/variables/painted-text-field';
import {
  ANALYST_PASS_GUIDE,
  ANALYST_PASS_READING_LABEL,
  READING_PALETTE_FULL,
  isGenericPassReading,
  readingPaletteEmpty,
  readingPaletteGroups,
  readingRefusalSentence,
} from '@/lib/analyst-reading';
import { firstPaletteItem, protocolVariableDescriber } from '@/lib/protocol-step-draft';
import type { VariableZone } from '@/lib/variable-pill';
import { READING_PLACE } from '@/lib/variable-pill';
import type { AnalystPass, ProtocolStepDraft } from '@matter_extractor/shared';
import { ANALYST_READING_MAX, hasAnalystReading, variableToken } from '@matter_extractor/shared';
import { useId, useMemo, useState } from 'react';

/**
 * Le champ de la lecture d'UNE passe (analyste.md §3.3 ; planche `AnalysteA`) : son
 * libellé et son guide au-dessus, le champ peint, et sous lui, à droite, le compte
 * « 612 / 4000 caractères ». Il y en a deux, montés ensemble par les onglets de
 * `analyst-readings.tsx` - la lecture de la passe 1 et celle de la passe 2 -, chacun
 * avec ses PROPRES marques (`PROTOCOL_TEST_IDS`), puisque Playwright refuse deux
 * correspondances.
 *
 * ## The field cites the objectives by `@` (issue #158, N33 overturned)
 *
 * It is the PAINTED FIELD of the directions (`PaintedTextField`, N55): `@` and the
 * `{ }` button open the directions' palette where that component opens it (W1), and a
 * citation is stored as the directions' token, `{{objectif:id}}`, drawn as a pill
 * under the objective's CURRENT name. What differs from a direction is what it
 * renders - the NAME, for the analyst, never the matter - and three things follow:
 *
 *   - the palette offers EVERY named objective of the protocol, grouped by step, with
 *     no rule of reach and no criterion (`readingPaletteGroups`, N48, N49) - a
 *     direction's offers its step and those before it, and the criteria;
 *   - the describer resolves objectives and nothing else: a criterion token typed
 *     here names nothing, and goes to the analyst as typed;
 *   - the zone stands at `READING_PLACE`, so a pill's fiche says « Rend ici, servi à
 *     l'analyste : le nom de l'objectif », and an ORPHAN's that the analyst will read
 *     « (objectif retiré) » - grey, like an orphan of a direction, and never a
 *     refusal (V2). Both passes serve the name the same way, so the place is one.
 *
 * Bounded at 4000 characters of STORED text - tokens included (`ANALYST_READING_MAX`,
 * the bound of a direction, so a text moved from the one to the other always fits) -
 * and REFUSED past it, never cut (review of !478): a keystroke, a paste or a pill
 * that would pass the bound does not happen (`maxLength`), and the palette greys the
 * citations that no longer fit, under `READING_PALETTE_FULL`. A refusal is SAID under
 * the field, in an `<output>` (role `status`) that stays mounted so a screen reader
 * hears it: the count is hidden from a screen reader, and a paste of 3500 characters
 * that does nothing, silently, reads as a broken field. The sentence holds for the
 * reading it was said on, and the next change the field takes clears it.
 *
 * It rides the autosaved draft like every other field of the protocol - `onChange`
 * is the layer's `onDraft`. The field has no counter of its own: this component draws
 * the line under it - the generic state, the refusal and the count.
 *
 * ## The guide says what that pass reads
 *
 * « Dis comment lire les extraits de ce protocole, objectif par objectif. » for the
 * first, « ...le discours entier de la personne... » for the second, then how to cite
 * an objective - the sentence of the guide above the field in the maquette, which is
 * also the placeholder of an EMPTY field. An empty reading is said by the yellow encart
 * of the section, which describes the field too.
 */

export interface AnalystReadingFieldProps {
  readonly pass: AnalystPass;
  /** The reading as typed - the draft's, or the version « Voir » is showing. */
  readonly reading: string;
  /**
   * The steps of that same content: what the field cites and names, and what the
   * panel resolves the citations on (#158, N50).
   */
  readonly steps: readonly ProtocolStepDraft[];
  /** « Voir », or a retired protocol: nothing is typable. */
  readonly readOnly: boolean;
  /** The id of the yellow encart when it is drawn: an EMPTY reading is described by it too. */
  readonly encartId: string | null;
  readonly onChange: (next: string) => void;
}

/** The marks of each field: the first keeps the ones of the reading of before #196, the second has its own. */
const IDS = {
  1: {
    field: PROTOCOL_TEST_IDS.readingField,
    insert: PROTOCOL_TEST_IDS.readingInsertVariable,
    palette: PROTOCOL_TEST_IDS.readingPalette,
    generic: PROTOCOL_TEST_IDS.readingGeneric,
    refused: PROTOCOL_TEST_IDS.readingRefused,
  },
  2: {
    field: PROTOCOL_TEST_IDS.styleReadingField,
    insert: PROTOCOL_TEST_IDS.styleReadingInsertVariable,
    palette: PROTOCOL_TEST_IDS.styleReadingPalette,
    generic: PROTOCOL_TEST_IDS.styleReadingGeneric,
    refused: PROTOCOL_TEST_IDS.styleReadingRefused,
  },
} as const;

export function AnalystReadingField({
  pass,
  reading,
  steps,
  readOnly,
  encartId,
  onChange,
}: AnalystReadingFieldProps) {
  const labelId = useId();
  const guideId = useId();
  const genericId = useId();
  const marks = IDS[pass];
  const empty = !hasAnalystReading(reading);
  const generic = isGenericPassReading(pass, reading);
  /** The last refusal of the bound, with the reading it was said on. */
  const [refusal, setRefusal] = useState<{
    readonly at: string;
    readonly sentence: string;
  } | null>(null);
  const describedBy = [
    guideId,
    generic ? genericId : null,
    empty && encartId !== null ? encartId : null,
  ]
    .filter((id): id is string => id !== null)
    .join(' ');

  /*
    Memoised on the steps, for the reason `direction-field.tsx` gives: the
    painted field repaints when its describer's answers change and compares
    signatures otherwise. The criteria are an empty list ON PURPOSE - only
    objectives are cited in a reading (N48), so a criterion token names nothing
    here, where the same describer names it in a direction.
  */
  const zone = useMemo<VariableZone>(
    () => ({
      describe: protocolVariableDescriber(steps, []),
      place: READING_PLACE,
      experienceId: null,
    }),
    [steps],
  );

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex flex-col gap-0.5">
        <p id={labelId} className="font-semibold text-gray-800 text-small">
          {ANALYST_PASS_READING_LABEL[pass]}
        </p>
        <p id={guideId} className="text-caption text-gray-600 leading-4">
          {ANALYST_PASS_GUIDE[pass]}
          {readOnly ? null : (
            <>
              {' '}
              Tape <span className="font-semibold">@</span> pour citer un objectif : la passe lira
              son nom.
            </>
          )}
        </p>
      </div>
      <PaintedTextField
        value={reading}
        onChange={onChange}
        maxLength={ANALYST_READING_MAX}
        onRefused={(refused) =>
          setRefusal({ at: reading, sentence: readingRefusalSentence(refused) })
        }
        readOnly={readOnly}
        placeholder={ANALYST_PASS_GUIDE[pass]}
        ariaLabelledBy={labelId}
        ariaDescribedBy={describedBy}
        testId={marks.field}
        insertTestId={marks.insert}
        minHeightClass="min-h-60"
        className="flex min-h-0 flex-col"
        zone={zone}
        renderMenu={(query, insert, fits) => (
          <VariablePalette
            groups={readingPaletteGroups(steps, query ?? '')}
            query={query}
            empty={readingPaletteEmpty(steps, query ?? '')}
            testId={marks.palette}
            fits={(variable) => fits(variableToken(variable))}
            full={READING_PALETTE_FULL}
            onPick={(variable) => insert(variableToken(variable))}
          />
        )}
        firstToken={(query) => {
          const first = firstPaletteItem(readingPaletteGroups(steps, query));
          return first === null ? null : variableToken(first.variable);
        }}
      />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          {generic ? (
            <p
              id={genericId}
              data-testid={marks.generic}
              className="text-caption text-gray-600 leading-4"
            >
              Lecture générique, préremplie : adaptez-la aux objectifs de ce protocole.
            </p>
          ) : null}
          {/* An `<output>`, whose role is `status`, mounted while the field is typable and EMPTY
              until a refusal: a live region that appears with its text is one some screen
              readers never announce. */}
          {readOnly ? null : (
            <output
              data-testid={marks.refused}
              className="block font-semibold text-caption text-rouge-deep leading-4"
            >
              {refusal !== null && refusal.at === reading ? refusal.sentence : ''}
            </output>
          )}
        </div>
        {/* Hidden from assistive technology: a count re-read at every keystroke is noise,
            and the field refuses what would pass its bound anyway. */}
        <p aria-hidden="true" className="shrink-0 text-right text-caption text-gray-600 leading-4">
          {reading.length} / {ANALYST_READING_MAX} caractères
        </p>
      </div>
    </div>
  );
}
